import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { useRestaurantExtras } from "../hooks/useRestaurantExtras";
import { ExtraDialog } from "./ExtraDialog";
import { DeleteExtraDialog } from "./DeleteExtraDialog";
import { restaurantExtrasApi } from "@/api/restaurantExtras";
import type { RestaurantExtra } from "@/types";

function formatCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: currency,
  }).format(amount);
}

export function ExtrasView() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedExtra, setSelectedExtra] = useState<RestaurantExtra | null>(
    null
  );

  const { data: extras, isLoading, error } = useRestaurantExtras();
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: restaurantExtrasApi.deleteExtra,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["restaurantExtras"] });
      setDeleteDialogOpen(false);
      setSelectedExtra(null);
    },
  });

  const handleEdit = (extra: RestaurantExtra) => {
    setSelectedExtra(extra);
    setDialogOpen(true);
  };

  const handleDelete = (extra: RestaurantExtra) => {
    setSelectedExtra(extra);
    setDeleteDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedExtra(null);
  };

  const handleDeleteConfirm = () => {
    if (selectedExtra) {
      deleteMutation.mutate(selectedExtra.id);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-10 w-36" />
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          Failed to load extras. Please try again later.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Extras</h1>
          <p className="text-muted-foreground">
            Manage reusable add-ons for your products
          </p>
        </div>
        <Button onClick={() => setDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Extra
        </Button>
      </div>

      {extras && extras.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <p className="mb-4 text-muted-foreground">
              You don't have any extras yet
            </p>
            <Button onClick={() => setDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Create your first extra
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {extras?.map((extra) => (
            <Card key={extra.id}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <CardTitle className="text-lg">{extra.name}</CardTitle>
                    <p className="text-lg font-semibold text-primary">
                      {formatCurrency(extra.price, extra.currency)}
                    </p>
                    {extra.productsUsingCount !== undefined &&
                      extra.productsUsingCount > 0 && (
                        <Badge variant="secondary" className="mt-2">
                          Used in {extra.productsUsingCount} product
                          {extra.productsUsingCount > 1 ? "s" : ""}
                        </Badge>
                      )}
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(extra)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(extra)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
            </Card>
          ))}
        </div>
      )}

      <ExtraDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        extra={selectedExtra}
      />

      <DeleteExtraDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedExtra(null);
        }}
        onConfirm={handleDeleteConfirm}
        isLoading={deleteMutation.isPending}
        extraName={selectedExtra?.name}
        productsUsingCount={selectedExtra?.productsUsingCount}
      />
    </div>
  );
}
